import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLeague, useAuth } from '@shootout-comps/core';
import type { League, LeagueFormat } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';
import DivisionTabs from '../../components/DivisionTabs';
import ManageTeamAdminsDialog from '../../components/ManageTeamAdminsDialog';

interface LibraryPlayer { _id: string; name: string; }

interface DivisionTeamsPageProps {
  leagueId: string;
  divisionId: string;
  onBack: () => void;
  onSwitchToFixtures: () => void;
  onSwitchToStandings: () => void;
  onSwitchToHistory: () => void;
  onOpenTeam: (teamId: string) => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

export default function DivisionTeamsPage({ leagueId, divisionId, onBack, onSwitchToFixtures, onSwitchToStandings, onSwitchToHistory, onOpenTeam }: DivisionTeamsPageProps) {
  const { fetchLeague, updateDivision, deleteDivision, addTeam, updateTeam, deleteTeam, addRosterPlayer, updateRosterPlayer, removeRosterPlayer, moveRosterPlayer, linkRosterUser, cancelPendingLink, unlinkRosterUser, linkRosterStandalonePlayer, unlinkRosterStandalonePlayer, approveRosterClaim, rejectRosterClaim } = useLeague();
  const { getAccessToken } = useAuth();
  const { t } = useLang();

  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [showAddTeam, setShowAddTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);

  // playerId -> draft roster-add form state, keyed by teamId
  const [rosterDraft, setRosterDraft] = useState<Record<string, { name: string; handicap: string }>>({});
  // teamId -> that team's "Player name" add-roster input, so we can refocus it after each add —
  // lets an organiser keep pressing Enter to add players back-to-back without reaching for the
  // mouse.
  const addPlayerNameRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Explicit expand override per team, keyed by teamId — only meaningful once a team has at
  // least one player (an empty roster always shows, since there's nothing to collapse and the
  // add-player box needs to stay visible to add the first one). Undefined means "collapsed"
  // (the default), so a team's roster only opens once the user asks to see it.
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null); // any in-flight teamId/playerId, for disabling controls
  const [managingAdminsFor, setManagingAdminsFor] = useState<string | null>(null); // teamId, or null when closed

  // Account/library-linking panel — expanded for at most one roster player at a time.
  const [linkingPlayerId, setLinkingPlayerId] = useState<string | null>(null);
  const [linkUsername, setLinkUsername] = useState('');
  const [libraryPlayers, setLibraryPlayers] = useState<LibraryPlayer[] | null>(null);
  const [selectedLibraryId, setSelectedLibraryId] = useState('');

  // Move-to-another-team panel — expanded for at most one roster player at a time.
  const [movingPlayerId, setMovingPlayerId] = useState<string | null>(null);
  const [moveTargetTeamId, setMoveTargetTeamId] = useState('');

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);

  const handleAddTeam = async () => {
    if (!teamName.trim()) return;
    setAddingTeam(true); setError('');
    try {
      const updated = await addTeam(leagueId, divisionId, teamName.trim());
      setLeague(updated);
      setTeamName(''); setShowAddTeam(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add team');
    } finally {
      setAddingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    setBusy(teamId); setError('');
    try {
      const updated = await deleteTeam(leagueId, divisionId, teamId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete team');
    } finally {
      setBusy(null);
    }
  };

  const handleRenameTeam = async (teamId: string, name: string) => {
    if (!name.trim()) return;
    try {
      const updated = await updateTeam(leagueId, divisionId, teamId, { name: name.trim() });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename team');
    }
  };

  const handleAddPlayer = async (teamId: string) => {
    const draft = rosterDraft[teamId] ?? { name: '', handicap: '' };
    if (!draft.name.trim()) return;
    setBusy(teamId); setError('');
    try {
      const handicap = draft.handicap.trim() ? Number(draft.handicap) : undefined;
      const updated = await addRosterPlayer(leagueId, divisionId, teamId, { name: draft.name.trim(), handicap });
      setLeague(updated);
      setRosterDraft(prev => ({ ...prev, [teamId]: { name: '', handicap: '' } }));
      // Adding this team's first player would otherwise auto-collapse the roster it was just
      // added from (teams default to collapsed) — force it open and put focus back in the name
      // field so repeated Enter presses keep adding players without any manual refocusing.
      setExpandedTeams(prev => ({ ...prev, [teamId]: true }));
      setTimeout(() => addPlayerNameRefs.current[teamId]?.focus(), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add player');
    } finally {
      setBusy(null);
    }
  };

  const handleRemovePlayer = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await removeRosterPlayer(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove player');
    } finally {
      setBusy(null);
    }
  };

  const openMovePanel = (playerId: string) => {
    setMovingPlayerId(prev => (prev === playerId ? null : playerId));
    setMoveTargetTeamId('');
  };

  const handleMovePlayer = async (teamId: string, playerId: string) => {
    if (!moveTargetTeamId) return;
    setBusy(playerId); setError('');
    try {
      const updated = await moveRosterPlayer(leagueId, divisionId, teamId, playerId, moveTargetTeamId);
      setLeague(updated);
      setMovingPlayerId(null); setMoveTargetTeamId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to move player');
    } finally {
      setBusy(null);
    }
  };

  const handleUpdateHandicap = async (teamId: string, playerId: string, handicap: number) => {
    setBusy(playerId); setError('');
    try {
      const updated = await updateRosterPlayer(leagueId, divisionId, teamId, playerId, { handicap });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update handicap');
    } finally {
      setBusy(null);
    }
  };

  const handleRenamePlayer = async (teamId: string, playerId: string, name: string) => {
    if (!name.trim()) return;
    setBusy(playerId); setError('');
    try {
      const updated = await updateRosterPlayer(leagueId, divisionId, teamId, playerId, { name: name.trim() });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename player');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleCaptain = async (teamId: string, playerId: string, currentCaptain: string | null | undefined) => {
    setBusy(playerId); setError('');
    try {
      const updated = await updateTeam(leagueId, divisionId, teamId, { captainPlayerId: currentCaptain === playerId ? null : playerId });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set captain');
    } finally {
      setBusy(null);
    }
  };

  const openLinkPanel = async (playerId: string) => {
    setLinkingPlayerId(prev => (prev === playerId ? null : playerId));
    setLinkUsername('');
    setSelectedLibraryId('');
    // Only the league owner's own player library is linkable here (PlayerModel.ownerId is the
    // library owner, not necessarily whichever admin is browsing — see backend note on
    // link-player) — this fetch simply returns empty for an admin who isn't the owner.
    if (libraryPlayers === null) {
      try {
        const token = await getAccessToken();
        const res = await fetch('/api/players/all', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json();
        setLibraryPlayers(Array.isArray(data) ? data.map((p: { _id: string; name: string }) => ({ _id: p._id, name: p.name })) : []);
      } catch {
        setLibraryPlayers([]);
      }
    }
  };

  const handleLinkUser = async (teamId: string, playerId: string) => {
    if (!linkUsername.trim()) return;
    setBusy(playerId); setError('');
    try {
      const updated = await linkRosterUser(leagueId, divisionId, teamId, playerId, linkUsername.trim());
      setLeague(updated); setLinkUsername('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link account');
    } finally {
      setBusy(null);
    }
  };

  const handleCancelPendingLink = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await cancelPendingLink(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel pending link');
    } finally {
      setBusy(null);
    }
  };

  const handleUnlinkUser = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await unlinkRosterUser(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to unlink account');
    } finally {
      setBusy(null);
    }
  };

  const handleApproveClaim = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await approveRosterClaim(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to approve claim');
    } finally {
      setBusy(null);
    }
  };

  const handleRejectClaim = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await rejectRosterClaim(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reject claim');
    } finally {
      setBusy(null);
    }
  };

  const handleLinkStandalone = async (teamId: string, playerId: string) => {
    if (!selectedLibraryId) return;
    setBusy(playerId); setError('');
    try {
      const updated = await linkRosterStandalonePlayer(leagueId, divisionId, teamId, playerId, selectedLibraryId);
      setLeague(updated); setSelectedLibraryId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link library player');
    } finally {
      setBusy(null);
    }
  };

  const handleUnlinkStandalone = async (teamId: string, playerId: string) => {
    setBusy(playerId); setError('');
    try {
      const updated = await unlinkRosterStandalonePlayer(leagueId, divisionId, teamId, playerId);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to unlink library player');
    } finally {
      setBusy(null);
    }
  };

  const handleSaveSettings = async (fields: Partial<{ framesPerFixture: number; maxHandicapDifference: number | null; pointsPerFrameWon: number; pointsPerMatchWin: number; pointsPerMatchDraw: number; pointsPerMatchLoss: number; tier: number; promotionSpots: number; relegationSpots: number }>) => {
    setSavingSettings(true); setError('');
    try {
      const updated = await updateDivision(leagueId, divisionId, fields);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteDivision = async () => {
    if (!window.confirm(`Delete "${division?.name}"? This removes all its teams and rosters.`)) return;
    setError('');
    try {
      await deleteDivision(leagueId, divisionId);
      onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete division');
    }
  };

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← {league.name}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)' }}>
          {division.name} <span style={{ fontSize: '0.9rem', color: 'var(--chalk-dim)', fontFamily: 'inherit' }}>{FORMAT_LABEL[division.format]}</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowSettings(s => !s)} style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', cursor: 'pointer' }}>
            ⚙ Settings
          </button>
          <button onClick={handleDeleteDivision} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
            Delete
          </button>
        </div>
      </div>

      <DivisionTabs active="teams" onSelect={tab => { if (tab === 'fixtures') onSwitchToFixtures(); else if (tab === 'standings') onSwitchToStandings(); else if (tab === 'history') onSwitchToHistory(); }} />

      {showSettings && (
        <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0 }}>
            Points formula: (frames won × Points/Frame) + a match win/draw/loss bonus. Leave the bonus fields at 0 for a pure frame-count table (the most common system).
          </p>
          {([
            ['framesPerFixture', 'Frames per fixture'],
            ['pointsPerFrameWon', 'Points / frame won'],
            ['pointsPerMatchWin', 'Bonus points / match win'],
            ['pointsPerMatchDraw', 'Bonus points / match draw'],
            ['pointsPerMatchLoss', 'Bonus points / match loss'],
          ] as const).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={{ flex: 1, fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>{label}</label>
              <input
                type="number" min={0}
                defaultValue={division[key]}
                onBlur={e => { const v = Number(e.target.value); if (!Number.isNaN(v) && v !== division[key]) handleSaveSettings({ [key]: v }); }}
                disabled={savingSettings}
                style={{ width: '80px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--chalk)', fontSize: '0.85rem', textAlign: 'center', outline: 'none' }}
              />
            </div>
          ))}

          {division.format === 'handicap' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={{ flex: 1, fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>Max handicap difference (starting-score cap)</label>
              <input
                type="number" min={0}
                defaultValue={division.maxHandicapDifference ?? ''}
                placeholder="No limit"
                onBlur={e => {
                  const raw = e.target.value;
                  const v = raw === '' ? null : Number(raw);
                  if (v !== null && Number.isNaN(v)) return;
                  if (v !== (division.maxHandicapDifference ?? null)) handleSaveSettings({ maxHandicapDifference: v });
                }}
                disabled={savingSettings}
                style={{ width: '80px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--chalk)', fontSize: '0.85rem', textAlign: 'center', outline: 'none' }}
              />
            </div>
          )}

          <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: '0.4rem 0 0', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.6rem' }}>
            {t.seasonManagementHint}
          </p>
          {([
            ['tier', t.tierFieldLabel],
            ['promotionSpots', t.promotionSpotsFieldLabel],
            ['relegationSpots', t.relegationSpotsFieldLabel],
          ] as const).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={{ flex: 1, fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>{label}</label>
              <input
                type="number" min={key === 'tier' ? 1 : 0}
                defaultValue={division[key] ?? (key === 'tier' ? 1 : 0)}
                onBlur={e => { const v = Number(e.target.value); if (!Number.isNaN(v) && v !== (division[key] ?? (key === 'tier' ? 1 : 0))) handleSaveSettings({ [key]: v }); }}
                disabled={savingSettings}
                style={{ width: '80px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--chalk)', fontSize: '0.85rem', textAlign: 'center', outline: 'none' }}
              />
            </div>
          ))}
        </div>
      )}

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '1.5rem 0 0.75rem', fontWeight: 600 }}>
        Teams ({division.teams.length})
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', marginBottom: '1.25rem' }}>
        {division.teams.map(team => {
          const draft = rosterDraft[team._id] ?? { name: '', handicap: '' };
          const hasPlayers = team.roster.length > 0;
          const isExpanded = !hasPlayers || expandedTeams[team._id] === true;
          return (
            <div key={team._id} style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: isExpanded ? '0.75rem' : 0 }}>
                {hasPlayers && (
                  <button
                    onClick={() => setExpandedTeams(prev => ({ ...prev, [team._id]: !isExpanded }))}
                    title={isExpanded ? 'Collapse roster' : 'Expand roster'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--chalk-dim)', fontSize: '0.75rem', padding: '0.1rem 0.2rem', flexShrink: 0 }}
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>
                )}
                <input
                  defaultValue={team.name}
                  onBlur={e => handleRenameTeam(team._id, e.target.value)}
                  style={{ fontWeight: 700, fontSize: '0.95rem', background: 'transparent', border: 'none', borderBottom: '1px dashed transparent', color: 'var(--chalk)', outline: 'none', flex: 1, padding: '0.1rem 0' }}
                  onFocus={e => { e.currentTarget.style.borderBottomColor = 'rgba(201,168,76,0.4)'; }}
                />
                {hasPlayers && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', flexShrink: 0 }}>
                    {team.roster.length} player{team.roster.length === 1 ? '' : 's'}
                  </span>
                )}
                <button onClick={() => onOpenTeam(team._id)} style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px', padding: '0.3rem 0.65rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  📅 Fixtures
                </button>
                <button onClick={() => setManagingAdminsFor(team._id)} style={{ background: 'rgba(155,89,182,0.12)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '6px', padding: '0.3rem 0.65rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  👤 Admins
                </button>
                <button onClick={() => handleDeleteTeam(team._id)} disabled={busy === team._id} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.3rem 0.65rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  {busy === team._id ? '…' : 'Delete Team'}
                </button>
              </div>

              {isExpanded && (
              <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.6rem' }}>
                {team.roster.length === 0 && (
                  <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', margin: 0 }}>No players yet.</p>
                )}
                {team.roster.map(p => (
                  <div key={p._id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', padding: '0.4rem 0.6rem' }}>
                      <button
                        onClick={() => handleToggleCaptain(team._id, p._id, team.captainPlayerId)}
                        title={team.captainPlayerId === p._id ? 'Captain — click to unset' : 'Set as captain'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: 0, opacity: team.captainPlayerId === p._id ? 1 : 0.3 }}
                      >
                        ⭐
                      </button>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: '0.3rem' }}>
                        <input
                          defaultValue={p.name}
                          onBlur={e => handleRenamePlayer(team._id, p._id, e.target.value)}
                          disabled={busy === p._id}
                          style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', background: 'transparent', border: 'none', borderBottom: '1px dashed transparent', color: 'var(--chalk)', outline: 'none', padding: '0.1rem 0' }}
                          onFocus={e => { e.currentTarget.style.borderBottomColor = 'rgba(201,168,76,0.4)'; }}
                        />
                        {p.pendingClaimUserId && <span title="Pending claim awaiting approval" style={{ fontSize: '0.7rem', flexShrink: 0 }}>⏳</span>}
                        {p.pendingLinkUserId && <span title="Link sent — awaiting the account holder's confirmation by email" style={{ fontSize: '0.7rem', flexShrink: 0 }}>✉️</span>}
                      </div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)' }}>hcp</span>
                      <input
                        type="number" defaultValue={p.handicap ?? 0}
                        onBlur={e => { const v = Number(e.target.value); if (!Number.isNaN(v) && v !== (p.handicap ?? 0)) handleUpdateHandicap(team._id, p._id, v); }}
                        disabled={busy === p._id}
                        style={{ width: '50px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0.15rem 0.3rem', color: 'var(--chalk)', fontSize: '0.72rem', textAlign: 'center', outline: 'none' }}
                      />
                      <button
                        onClick={() => openLinkPanel(p._id)}
                        title="Link to an account or player-library entry"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.15rem', opacity: (p.linkedUserId || p.linkedPlayerId || p.pendingClaimUserId) ? 1 : 0.35 }}
                      >
                        🔗
                      </button>
                      {division.teams.length > 1 && (
                        <button
                          onClick={() => openMovePanel(p._id)}
                          title="Move to another team"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', fontWeight: 700, lineHeight: 1, padding: '0 0.15rem', color: movingPlayerId === p._id ? 'var(--gold)' : 'var(--chalk-dim)' }}
                        >
                          ⇄
                        </button>
                      )}
                      <button onClick={() => handleRemovePlayer(team._id, p._id)} disabled={busy === p._id} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '0.9rem', padding: '0 0.2rem' }}>
                        {busy === p._id ? '…' : '✕'}
                      </button>
                    </div>
                    {movingPlayerId === p._id && (
                      <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '0.6rem 0.7rem', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)' }}>Move to:</span>
                        <select
                          value={moveTargetTeamId}
                          onChange={e => setMoveTargetTeamId(e.target.value)}
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '5px', padding: '0.25rem 0.5rem', color: 'var(--chalk)', fontSize: '0.75rem', outline: 'none', maxWidth: '160px' }}
                        >
                          <option value="">Select team</option>
                          {division.teams.filter(tm => tm._id !== team._id).map(tm => <option key={tm._id} value={tm._id}>{tm.name}</option>)}
                        </select>
                        <button
                          onClick={() => handleMovePlayer(team._id, p._id)}
                          disabled={!moveTargetTeamId || busy === p._id}
                          style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '5px', padding: '0.25rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', opacity: !moveTargetTeamId ? 0.5 : 1 }}
                        >
                          {busy === p._id ? '…' : 'Move'}
                        </button>
                        <button
                          onClick={() => setMovingPlayerId(null)}
                          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '5px', padding: '0.25rem 0.6rem', fontSize: '0.7rem', color: 'var(--chalk-dim)', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {linkingPlayerId === p._id && (
                      <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '0.6rem 0.7rem', marginTop: '0.3rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', minWidth: '90px' }}>Account:</span>
                          {p.linkedUserId ? (
                            <>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gold)' }}>🔗 linked</span>
                              <button onClick={() => handleUnlinkUser(team._id, p._id)} disabled={busy === p._id} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer' }}>
                                Unlink
                              </button>
                            </>
                          ) : p.pendingClaimUserId ? (
                            <>
                              <span style={{ fontSize: '0.75rem', color: '#e67e22' }}>{t.pendingClaimApprovalLabel}</span>
                              <button onClick={() => handleApproveClaim(team._id, p._id)} disabled={busy === p._id} style={{ background: 'rgba(46,204,113,0.12)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}>
                                {t.approveBtn}
                              </button>
                              <button onClick={() => handleRejectClaim(team._id, p._id)} disabled={busy === p._id} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer' }}>
                                {t.rejectBtn}
                              </button>
                            </>
                          ) : p.pendingLinkUserId ? (
                            <>
                              <span style={{ fontSize: '0.75rem', color: '#e67e22' }} title="Waiting for the account holder to confirm via the email we sent them">✉️ Awaiting confirmation</span>
                              <button onClick={() => handleCancelPendingLink(team._id, p._id)} disabled={busy === p._id} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <input
                                value={linkUsername}
                                onChange={e => setLinkUsername(e.target.value)}
                                placeholder="username"
                                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '5px', padding: '0.25rem 0.5rem', color: 'var(--chalk)', fontSize: '0.75rem', outline: 'none', width: '140px' }}
                              />
                              <button onClick={() => handleLinkUser(team._id, p._id)} disabled={busy === p._id || !linkUsername.trim()} style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '5px', padding: '0.25rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}>
                                Link
                              </button>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', minWidth: '90px' }}>Library player:</span>
                          {p.linkedPlayerId ? (
                            <>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gold)' }}>🔗 linked</span>
                              <button onClick={() => handleUnlinkStandalone(team._id, p._id)} disabled={busy === p._id} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer' }}>
                                Unlink
                              </button>
                            </>
                          ) : (
                            <>
                              <select
                                value={selectedLibraryId}
                                onChange={e => setSelectedLibraryId(e.target.value)}
                                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '5px', padding: '0.25rem 0.5rem', color: 'var(--chalk)', fontSize: '0.75rem', outline: 'none', maxWidth: '160px' }}
                              >
                                <option value="">{libraryPlayers === null ? 'Loading…' : libraryPlayers.length === 0 ? 'No library players' : 'Select player'}</option>
                                {(libraryPlayers ?? []).map(lp => <option key={lp._id} value={lp._id}>{lp.name}</option>)}
                              </select>
                              <button onClick={() => handleLinkStandalone(team._id, p._id)} disabled={busy === p._id || !selectedLibraryId} style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '5px', padding: '0.25rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}>
                                Link
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input
                  ref={el => { addPlayerNameRefs.current[team._id] = el; }}
                  value={draft.name}
                  onChange={e => setRosterDraft(prev => ({ ...prev, [team._id]: { ...draft, name: e.target.value } }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPlayer(team._id); } }}
                  placeholder="Player name"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.6rem', color: 'var(--chalk)', fontSize: '0.8rem', outline: 'none' }}
                />
                <input
                  value={draft.handicap}
                  onChange={e => setRosterDraft(prev => ({ ...prev, [team._id]: { ...draft, handicap: e.target.value } }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPlayer(team._id); } }}
                  placeholder="Hcp"
                  type="number"
                  style={{ width: '60px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--chalk)', fontSize: '0.8rem', outline: 'none', textAlign: 'center' }}
                />
                <button onClick={() => handleAddPlayer(team._id)} disabled={busy === team._id || !draft.name.trim()} style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  + Add
                </button>
              </div>
              </>
              )}
            </div>
          );
        })}
      </div>

      {showAddTeam ? (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTeam(); } }}
            placeholder="Team name"
            autoFocus
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.6rem 0.8rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none' }}
          />
          <button onClick={handleAddTeam} disabled={addingTeam || !teamName.trim()} style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
            {addingTeam ? '…' : 'Add'}
          </button>
          <button onClick={() => setShowAddTeam(false)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.6rem 1rem', color: 'var(--chalk-dim)', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      ) : (
        <button onClick={() => setShowAddTeam(true)} style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold)', border: '1px dashed rgba(201,168,76,0.35)', borderRadius: '10px', padding: '0.75rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
          + Add Team
        </button>
      )}

      {managingAdminsFor && (() => {
        const team = division.teams.find(tm => tm._id === managingAdminsFor);
        if (!team) return null;
        return (
          <ManageTeamAdminsDialog
            leagueId={leagueId}
            divisionId={divisionId}
            teamId={team._id}
            teamName={team.name}
            onClose={() => setManagingAdminsFor(null)}
          />
        );
      })()}
    </div>
  );
}
